/** Error de la API en formato RFC 9457 (application/problem+json). */
export interface Problem {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code?: string;
  requestId?: string;
  errors?: { path: string; message: string; code: string }[];
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly fieldErrors: Record<string, string>;
  /** Datos adicionales del problema (p. ej. errores por fila de una importación). */
  readonly details?: Record<string, unknown>;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title ?? `Error ${problem.status}`);
    this.name = 'ApiError';
    this.status = problem.status;
    this.code = problem.code ?? `http.${problem.status}`;
    this.requestId = problem.requestId;
    this.fieldErrors = Object.fromEntries((problem.errors ?? []).map((e) => [e.path, e.message]));
    this.details = problem.details;
  }

  static async fromResponse(response: Response, body?: unknown): Promise<ApiError> {
    let problem: unknown = body;
    if (problem === undefined) {
      try {
        problem = await response.clone().json();
      } catch {
        problem = undefined;
      }
    }
    if (problem && typeof problem === 'object' && 'status' in problem)
      return new ApiError(problem as Problem);
    return new ApiError({
      status: response.status,
      detail: response.statusText || 'Error de comunicación con el servidor',
    });
  }
}

/** Nombre legible de los campos que más devuelve la API (el resto se muestra tal cual). */
const FIELD_LABELS: Record<string, string> = {
  name: 'Nombre',
  code: 'Código',
  warehouseId: 'Bodega',
  fromWarehouseId: 'Bodega de origen',
  toWarehouseId: 'Bodega de destino',
  customerId: 'Cliente',
  supplierId: 'Proveedor',
  categoryId: 'Categoría',
  variantId: 'Producto',
  quantity: 'Cantidad',
  unitCost: 'Costo',
  price: 'Precio',
  sku: 'SKU',
  variants: 'Variantes',
  cost: 'Costo',
  reorderPoint: 'Punto de reorden',
  email: 'Correo',
  phone: 'Teléfono',
  amount: 'Valor',
  subtotal: 'Subtotal',
  description: 'Descripción',
  lines: 'Productos',
};

function fieldLabel(path: string): string {
  const parts = path.split('.').filter((p) => !/^\d+$/.test(p));
  const last = parts[parts.length - 1] ?? path;
  return FIELD_LABELS[last] ?? last;
}

/**
 * Mensaje del error. Con `withFields`, un error de validación agrega qué campos fallaron: un aviso
 * suelto («La solicitud contiene datos inválidos») no le dice a la persona qué corregir.
 */
export function errorMessage(error: unknown, options: { withFields?: boolean } = {}): string {
  if (error instanceof ApiError) {
    // El API dice qué permiso falta en código (accounting.read); al usuario, en palabras.
    if (error.code === 'auth.missing_permissions')
      return 'No tienes permiso para ver o hacer esto. Pídeselo a quien administra la cuenta.';
    const fields = Object.entries(error.fieldErrors);
    if (!options.withFields || fields.length === 0) return error.message;
    const detail = fields
      .slice(0, 3)
      .map(([path, message]) => `${fieldLabel(path)}: ${message}`)
      .join(' · ');
    return `${error.message} — ${detail}`;
  }
  if (error instanceof Error) return error.message;
  return 'Ocurrió un error inesperado';
}
